import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Mui from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';

import { PEDIDO_SITUACAO_ENUM, PEDIDO_TIPO_FATURAMENTO, STATUS_MONITOR_PEDIDO, TIPO_DOCUMENTO, SHOW_SHORTCUTS } from '~/constants';
import { getApplicationIsOnline } from '~/store/modules/app/app-selectors';
import * as monitorActions from '~/store/modules/monitor/monitor-actions';

import PedidoTable from '../../components/PedidoTable';
import PedidoTimeline from '../../components/PedidoTimeline';
import ModalImportarDocumento from '~/pages/TelaVenda/modal/ModalImportarDocumento';
import { PedidoOnlineService } from '~/store/modules/pedido/pedido-service';
import { ordenarPorRelevancia, TIPO_PESQUISA } from '~/utils/weighted-search';
import { getFilterByData } from '~/store/modules/monitor/monitor-helper';
import monitorService from '~/store/modules/monitor/monitor-service';

const useStyles = Mui.makeStyles(theme => ({
  badgeZero: {
    '&.MuiBadge-badge': {
      backgroundColor: theme.palette.background.darkest,
      color: theme.palette.text.primary,
    },
  },
  badgeMany: {
    '&.MuiBadge-badge': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
}));

const Pedido = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const anchorRef = useRef();
  const [searchTerm, setSearchTerm] = useState('');

  const isOnline = useSelector(getApplicationIsOnline())
  const { terminal } = useSelector(state => state.app);
  const {
    loading,
    filtroData,
    filtroDataOptions,
    status,
    statusOptions,
    totalizadores,
    showTimelinePedido,
    page,
    rowsPerPage
  } = useSelector(state => state.monitor);
  const [showModalImportarDocumento, setShowModalImportarDocumento] = useState({
    show: false
  });

  const handleTabsChange = (event, value) => {
    dispatch(monitorActions.setStatus(value));
  };

  const handleSearch = async (goBackToFirstPage) => {
    try {
      dispatch(monitorActions.setLoading(true));

      let conditions = []
      let orderby = ''

      let tipoPesquisa = null
      if (/^\d+$/.test(searchTerm)) { // números
        tipoPesquisa = TIPO_PESQUISA.NUMEROS_INTEIROS
        orderby = `numero asc, xPed asc, xCont asc, valorTotal asc`
        conditions.push(`contains(cast(numero, Edm.String),'${searchTerm}')`)
        conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm}')`)
        conditions.push(`contains(xPed,'${searchTerm}')`)
        conditions.push(`contains(xCont,'${searchTerm}')`)
      }
      else if (/^(?=.*\d)(?=.*[.,])[\d.,]+$/.test(searchTerm)) { // numeros + ponto ou virgula
        tipoPesquisa = TIPO_PESQUISA.NUMEROS_FLUTUANTES
        orderby = `valorTotal asc`
        conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm.replace(/,/g, ".")}')`)
      }
      else if (/^[\p{L}\s]+$/u.test(searchTerm)) { // texto
        tipoPesquisa = TIPO_PESQUISA.TEXTO
        orderby = `clienteNome asc, fatVendedor/nome asc`
        conditions.push(`contains(clienteNome,'${searchTerm}')`)
        conditions.push(`contains(fatVendedor/nome,'${searchTerm}')`)
      }
      else { // misto
        tipoPesquisa = TIPO_PESQUISA.MISTO
        orderby = `numero asc, clienteNome asc, fatVendedor/nome asc, xPed asc, xCont asc`
        conditions.push(`contains(cast(numero, Edm.String),'${searchTerm}')`)
        conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm}')`)
        conditions.push(`contains(xPed,'${searchTerm}')`)
        conditions.push(`contains(xCont,'${searchTerm}')`)
        conditions.push(`contains(clienteNome,'${searchTerm}')`)
        conditions.push(`contains(fatVendedor/nome,'${searchTerm}')`)
      }

      let filter = [conditions.length ? `(${conditions.join(' or ')})` : '']
      filter.push(...getFilterByData(filtroData, 'dataEmissao'))
      filter.push(`(fisOperacao/documentoFinalidade ne 4)`)

      switch (status) {
        case STATUS_MONITOR_PEDIDO.ABERTOS:
          filter.push(`(cancelado eq false)`)
          filter.push(`(situacao eq ${PEDIDO_SITUACAO_ENUM.Aberto} or situacao eq ${PEDIDO_SITUACAO_ENUM.ParcialmenteFaturado})`)
          break;
        case STATUS_MONITOR_PEDIDO.BLOQUEADOS:
          filter.push(`(cancelado eq false)`)
          filter.push(`(situacao eq ${PEDIDO_SITUACAO_ENUM.Bloqueado})`)
          break;
        case STATUS_MONITOR_PEDIDO.FATURADOS:
          filter.push(`(situacao eq ${PEDIDO_SITUACAO_ENUM.Fechado})`)
          filter.push(`(faturado eq ${PEDIDO_TIPO_FATURAMENTO.FaturadoCupomFiscal} or faturado eq ${PEDIDO_TIPO_FATURAMENTO.FaturadoDocumentoEletronico})`)
          break;
        case STATUS_MONITOR_PEDIDO.CANCELADOS:
          filter.push(`(cancelado eq true)`)
          break;
        case STATUS_MONITOR_PEDIDO.REJEITADOS:
          filter.push(`(situacao eq ${PEDIDO_SITUACAO_ENUM.RejeitadoCredito} or situacao eq ${PEDIDO_SITUACAO_ENUM.RejeitadoEstoque} or situacao eq ${PEDIDO_SITUACAO_ENUM.RejeitadoRegraComercialNegocio} or situacao eq ${PEDIDO_SITUACAO_ENUM.RejeitadoPorRegraComercialDesconto})`)
          break;
        case STATUS_MONITOR_PEDIDO.PENDETES:
          filter.push(`(situacao eq ${PEDIDO_SITUACAO_ENUM.ParcialmenteFaturado})`)
          break;
        case STATUS_MONITOR_PEDIDO.FECHADOS:
          filter.push(`(situacao eq ${PEDIDO_SITUACAO_ENUM.Fechado})`)
          break;
      }

      let newPage = goBackToFirstPage ? 0 : page

      const service = new PedidoOnlineService();
      const res = await service.getAllPedidos({
        skip: newPage === 0 ? 0 : newPage * rowsPerPage,
        top: rowsPerPage,
        filter: filter,
        orderby: orderby,
      });
      
      if (goBackToFirstPage) {
        dispatch(monitorActions.setPage(0));
      }

      const items = ordenarPorRelevancia(res.data.items, searchTerm, TIPO_DOCUMENTO.PEDIDO, tipoPesquisa)

      let totalizadores = {}
      const { data } = await service.getTotalizadores(filtroData, searchTerm);
      totalizadores = { ...data };

      dispatch(monitorActions.loadPedidosSearchSuccess({
        showTimelinePedido: false,
        pedidoHistoricos: [],
        pedidos: items,
        totalizadores: totalizadores,
        quantidadeDocumentos: res.data.count ?? items.length,
        valorTotalDocumentos: items.reduce((total, item) => total + item.valorTotal, 0)
      }))

    } catch (err) {
      throw err;
    } finally {
      dispatch(monitorActions.setLoading(false));
    }
  };

  useEffect(() => {
    if (showModalImportarDocumento.show) {
      return
    }
    if(searchTerm) {
      handleSearch(false)
    } 
    else {
      dispatch(monitorActions.loadPedidosRequest());
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (showModalImportarDocumento.show) {
      return
    }
    if(searchTerm) {
      handleSearch(true)
    } 
    else {
      dispatch(monitorActions.setPage(0));
      dispatch(monitorActions.loadPedidosRequest());
    }
  }, [status, filtroData]);

  useEffect(() => {
    dispatch(monitorActions.setStatus('abertos'));
    return () => {
      dispatch(monitorActions.limparDados());
    };
  }, [isOnline]);

  // useEffect(() => {
  //   if (searchTerm) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 1000)
  
  //     return () => clearTimeout(delayDebounceFn)
  //   } else {
  //     dispatch(monitorActions.loadPedidosRequest());
  //   }
  // }, [searchTerm])

  return (
    <>
      <Mui.Grid container>
        <Mui.Grid item md={12}>
          <Mui.Box p={2} display="flex" justifyContent="space-between">
            <Mui.Box style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginRight: '16px', flex: '0 1 auto' }}>
              <Mui.Button
                variant="contained"
                className='button-primary'
                disabled={terminal === null}
                style={{ height: 44, whiteSpace: 'normal' }}
                onClick={() => {
                  history.push('/pdv', {
                    tipoDocumento: TIPO_DOCUMENTO.PEDIDO
                  });
                }}
              >
                <span variant="span" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MuiIcons.AddCircle style={{position: 'relative', bottom: '2px'}}/>
                  <span style={{ verticalAlign: 'middle' }}>Novo Pedido {SHOW_SHORTCUTS && '(CTRL+P)'}</span>
                </span>
              </Mui.Button>
              <Mui.Button
                variant="contained"
                className='button-white'
                disabled={terminal === null}
                style={{ height: 44, whiteSpace: 'normal' }}
                onClick={() => {
                  dispatch(monitorActions.setPage(0));
                  dispatch(monitorActions.setRowsPerPage(10));
                  setShowModalImportarDocumento({ show: true })
                }}
              >
                <span variant="span" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MuiIcons.ImportExport style={{position: 'relative', bottom: '2px'}}/>
                  <span style={{ verticalAlign: 'middle' }}>Importar</span>
                </span>
              </Mui.Button>
            </Mui.Box>

            <Mui.Box display="flex" alignItems={'flex-start'} style={{ flex: '1 1 auto' }}>
              <Mui.Tooltip title="Buscar por nº do documento, cliente, vendedor, pedido de compra, contrato ou valor...">
                <Mui.Box display="inline-block" style={{flex: '1 0 auto'}}>
                  <Mui.TextField
                    disabled={loading}
                    style={{width: '100%'}}
                    InputProps={{
                      style: {
                        height: 44
                      },
                      startAdornment: (
                        <Mui.InputAdornment position="start">
                          <Mui.SvgIcon fontSize="small" color="action">
                            <MuiIcons.Search />
                          </Mui.SvgIcon>
                        </Mui.InputAdornment>
                      )
                    }}
                    placeholder="Pesquisar pedido..."
                    variant="outlined"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    onKeyPress={(event) => {
                      if (event.key === 'Enter') {
                        if (searchTerm.trim() === '') {
                          setSearchTerm('')
                          dispatch(monitorActions.setPage(0));
                          dispatch(monitorActions.loadPedidosRequest());
                        } else {
                          handleSearch(true);
                        }
                      }
                    }}
                  />
                </Mui.Box>
              </Mui.Tooltip>
              <Mui.ButtonGroup
                variant="contained"
                color="default"
                ref={anchorRef}
                aria-label="split button"
                style={{ marginLeft: 16, minHeight: 44, flex: '0 1 auto' }}
              >
                {filtroDataOptions.map((f, index) => (
                  <Mui.Button
                    key={`pedido-filtro-data-${index}`}
                    color={f.filtro === filtroData ? 'primary' : 'default'}
                    style={{ whiteSpace: 'normal' }}
                    className={f.filtro === filtroData ? 'button-primary' : 'button-white'}
                    onClick={() => dispatch(monitorActions.setFiltroData(f.filtro))}
                  >
                    {f.titulo}
                  </Mui.Button>
                ))}
              </Mui.ButtonGroup>
            </Mui.Box>
          </Mui.Box>
          <Mui.Tabs
            onChange={handleTabsChange}
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            value={status}
            variant="scrollable"
          >
            {statusOptions
              ?.map(s => ({ ...s, count: totalizadores ? totalizadores[s?.value] ?? 0 : 0 }))
              ?.map(tab => (
                <Mui.Tab
                  key={tab?.value}
                  value={tab?.value}
                  label={
                    <Mui.Box>
                      <Mui.Badge
                        badgeContent={tab?.count ?? 0}
                        color="default"
                        classes={{
                          badge:
                            (tab?.count ?? 0) === 0
                              ? classes.badgeZero
                              : classes.badgeMany,
                        }}
                        max={9999}
                        showZero
                      />
                      <Mui.Typography>{tab?.label}</Mui.Typography>
                    </Mui.Box>
                  }
                />
              ))}
          </Mui.Tabs>

          <Mui.Divider />

          <PedidoTable />
        </Mui.Grid>
      </Mui.Grid>
      {showTimelinePedido && <PedidoTimeline />}
      {showModalImportarDocumento.show && (
        <ModalImportarDocumento
          type={TIPO_DOCUMENTO.ORCAMENTO}
          show={showModalImportarDocumento.show}
          onClose={() => {
            dispatch(monitorActions.setPage(0));
            dispatch(monitorActions.setRowsPerPage(10));
            setShowModalImportarDocumento({ show: false })
          }}
          callback={data => {
            history.push('/pdv', data);
          }}
        />
      )}
    </>
  );
};

export default Pedido;
